/**
 * Notifications API service for managing user notifications and preferences.
 * @module lib/api/notifications.api
 */

import {
  BulkUpdateNotificationPreferencesDto,
  Notification,
  NotificationFeedResponse,
  NotificationPreference,
  NotificationQueryDto,
  NotificationSummary,
  PaginationParams,
  UnreadEntitiesResponse,
  UpdateNotificationPreferenceDto,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface NotificationListResponse {
  data: Notification[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface SmsStatus {
  phoneNumber: string | null
  phoneVerified: string | null
  consentGrantedAt: string | null
  consentRevokedAt: string | null
  consentVersion: string | null
  enabled: boolean
  disclosureVersion: string
}

class NotificationsApiService {
  async getSmsStatus(): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/status')
  }

  async requestSmsVerification(data: {
    phoneNumber: string
    consentAccepted: boolean
  }): Promise<{ sent: boolean; phoneNumber: string; expiresAt: string }> {
    return apiClient('/notifications/sms/verification', {
      method: 'POST',
      body: data,
    })
  }

  async confirmSmsVerification(data: {
    phoneNumber: string
    code: string
  }): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/verification/confirm', {
      method: 'POST',
      body: data,
    })
  }

  async revokeSmsConsent(): Promise<SmsStatus> {
    return apiClient<SmsStatus>('/notifications/sms/consent', {
      method: 'DELETE',
    })
  }

  /**
   * Gets paginated notifications
   * @param query - Filter and pagination parameters
   * @returns Paginated notification list
   */
  async getNotifications(
    query?: NotificationQueryDto & PaginationParams,
  ): Promise<NotificationListResponse> {
    const queryString = query ? buildQueryString(query) : ''
    return apiClient<NotificationListResponse>(`/notifications${queryString}`)
  }

  /**
   * Gets a single notification by ID
   * @param id - Notification ID
   * @returns The notification
   */
  async getNotification(id: string): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}`)
  }

  /**
   * Gets count of unread notifications
   * @returns Unread count
   */
  async getUnreadCount(): Promise<{ count: number }> {
    return apiClient<{ count: number }>('/notifications/unread-count')
  }

  /**
   * Gets notification summary
   * @returns Notification summary
   */
  /**
   * Gets the lane-based feed (action required / mentions / activity)
   */
  async getFeed(limit?: number): Promise<NotificationFeedResponse> {
    return apiClient<NotificationFeedResponse>(
      `/notifications/feed${limit ? `?limit=${limit}` : ''}`,
    )
  }

  /**
   * Gets per-record unread rollups (task/ticket) for row indicators
   */
  async getUnreadEntities(): Promise<UnreadEntitiesResponse> {
    return apiClient<UnreadEntitiesResponse>('/notifications/unread-entities')
  }

  async getSummary(): Promise<NotificationSummary> {
    return apiClient<NotificationSummary>('/notifications/summary')
  }

  /**
   * Marks a notification as read
   * @param id - Notification ID
   * @returns Updated notification
   */
  async markAsRead(id: string): Promise<Notification> {
    return apiClient<Notification>(`/notifications/${id}/read`, {
      method: 'PUT',
    })
  }

  /**
   * Marks all notifications as read
   * @returns Count of updated notifications
   */
  /**
   * Dismisses all undismissed notifications, optionally scoped to types
   * (used by the feed's per-lane "Dismiss all")
   */
  async dismissAll(types?: string[]): Promise<{ dismissed: number }> {
    return apiClient<{ dismissed: number }>('/notifications/dismiss-all', {
      method: 'POST',
      body: types?.length ? { types } : {},
    })
  }

  async markAllAsRead(): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>('/notifications/mark-all-read', {
      method: 'POST',
    })
  }

  /**
   * Marks multiple notifications as read
   * @param notificationIds - Array of notification IDs
   * @returns Count of updated notifications
   */
  async markMultipleAsRead(
    notificationIds: string[],
  ): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>('/notifications/mark-read', {
      method: 'POST',
      body: { notificationIds },
    })
  }

  /**
   * Dismisses a notification
   * @param id - Notification ID
   */
  async dismissNotification(id: string): Promise<void> {
    return apiClient<void>(`/notifications/${id}`, { method: 'DELETE' })
  }

  /**
   * Dismisses multiple notifications
   * @param notificationIds - Array of notification IDs
   * @returns Count of deleted notifications
   */
  async dismissMultiple(
    notificationIds: string[],
  ): Promise<{ deleted: number }> {
    return apiClient<{ deleted: number }>('/notifications/dismiss', {
      method: 'POST',
      body: { notificationIds },
    })
  }

  /**
   * Gets notification preferences
   * @returns Array of notification preferences
   */
  async getPreferences(): Promise<NotificationPreference[]> {
    return apiClient<NotificationPreference[]>('/notifications/preferences')
  }

  /**
   * Updates a single notification preference
   * @param data - Preference update data
   * @returns Updated preference
   */
  async updatePreference(
    data: UpdateNotificationPreferenceDto,
  ): Promise<NotificationPreference> {
    return apiClient<NotificationPreference>(
      `/notifications/preferences/${data.channel}/${data.type}`,
      { method: 'PUT', body: data },
    )
  }

  /**
   * Updates multiple notification preferences
   * @param data - Bulk update data
   * @returns Update result with preferences
   */
  async updateMultiplePreferences(
    data: BulkUpdateNotificationPreferencesDto,
  ): Promise<{ updated: number; preferences: NotificationPreference[] }> {
    return apiClient<{
      updated: number
      preferences: NotificationPreference[]
    }>('/notifications/preferences', { method: 'PUT', body: data })
  }

  /**
   * Sets default notification preferences
   * @returns Update result with preferences
   */
  async setDefaultPreferences(): Promise<{
    updated: number
    preferences: NotificationPreference[]
  }> {
    return apiClient<{
      updated: number
      preferences: NotificationPreference[]
    }>('/notifications/preferences/set-defaults', { method: 'POST' })
  }

  /**
   * Disables all notifications for a channel
   * @param channel - Channel name
   * @returns Count of updated preferences
   */
  async disableAllForChannel(channel: string): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>(
      `/notifications/preferences/${channel}/disable-all`,
      { method: 'POST' },
    )
  }

  /**
   * Enables all notifications for a channel
   * @param channel - Channel name
   * @returns Count of updated preferences
   */
  async enableAllForChannel(channel: string): Promise<{ updated: number }> {
    return apiClient<{ updated: number }>(
      `/notifications/preferences/${channel}/enable-all`,
      { method: 'POST' },
    )
  }
}

export const notificationsApi = createSingletonApi(NotificationsApiService)
